const currencyFormatterCache = new Map<string, Intl.NumberFormat>();

/**
 * Cached Intl currency formatter for an ISO 4217 code. Unknown/invalid codes
 * fall back to USD so display never throws.
 */
function getCurrencyFormatter(currency: string): Intl.NumberFormat {
  const code = (currency || 'USD').toUpperCase();
  const cached = currencyFormatterCache.get(code);
  if (cached) return cached;
  let formatter: Intl.NumberFormat;
  try {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  } catch {
    formatter = getCurrencyFormatter('USD');
  }
  currencyFormatterCache.set(code, formatter);
  return formatter;
}

const ACTIVE_CURRENCY_KEY = 'halo_store_currency';

function readInitialCurrency(): string {
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(ACTIVE_CURRENCY_KEY);
      if (stored) return stored.toUpperCase();
    }
  } catch {
    // localStorage unavailable (SSR / privacy mode) — fall back to USD
  }
  return 'USD';
}

// Ambient display currency for the active store. The money helpers default to
// this, so the existing formatCurrency(value) call sites render in the selected
// store's currency without per-site changes. Seeded synchronously from
// localStorage (so first paint is correct on refresh) and kept in sync by
// CurrencyProvider. Multi-store views pass an explicit currency to override it.
let activeCurrency = readInitialCurrency();

export function setActiveCurrency(currency: string | null | undefined): void {
  activeCurrency = (currency || 'USD').toUpperCase();
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(ACTIVE_CURRENCY_KEY, activeCurrency);
    }
  } catch {
    // ignore persistence failures
  }
}

export function getActiveCurrency(): string {
  return activeCurrency;
}

const numberFormatter = new Intl.NumberFormat('en-US');

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Format a monetary value in the given currency (ISO 4217, default USD).
 * Pass the store's display currency so non-USD shops (e.g. AUD) render with the
 * correct symbol instead of being mislabeled as USD.
 */
export function formatCurrency(value: number | null | undefined, currency?: string): string {
  const formatter = getCurrencyFormatter(currency ?? activeCurrency);
  return formatter.format(value ?? 0);
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return '0';
  return numberFormatter.format(value);
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null) return '0%';
  return percentFormatter.format(value / 100);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
